import { Modal } from "react-bootstrap";
import { OrderItem } from "../models/IOrders";
import { getFoodById } from "../services/food";
import { useEffect, useState } from "react";
import { IFood } from "../models/IFood";
import { formatDateToDayMonthYear, orderStatuses, parseMenuExtras } from "../utils/Util";
import { OrderStatus } from "../models/IOrderStatusList";
import { getTownById } from "../services/locations";
import { getCustomerDetails } from "../services/auth";

const TASK_META: Record<number, { label: string; icon: string; bg: string; color: string; border: string }> = {
  0: { label: 'Alındı',       icon: 'bi-bag-check-fill',     bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  1: { label: 'Teslim Edildi',icon: 'bi-check-circle-fill',  bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
  2: { label: 'Hazırlanıyor', icon: 'bi-fire',               bg: '#fffbeb', color: '#92400e', border: '#fde68a' },
  3: { label: 'Yolda',        icon: 'bi-bicycle',            bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
  4: { label: 'İptal',        icon: 'bi-x-circle-fill',      bg: '#fef2f2', color: '#991b1b', border: '#fecaca' },
};

function StandardModal({ isOpen, onClose, selectedItem }: {
  isOpen: boolean;
  onClose: () => void;
  selectedItem: OrderItem | null;
}) {
  const [foodData, setFoodData] = useState<IFood[]>([]);
  const [orderStatus, setOrderStatus] = useState<OrderStatus | null>(null);
  const [addressText, setAddressText] = useState<string>("");
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [customerDetail, setCustomerDetail] = useState<{ customerName: string; customerSurName: string; customerTelNo: string } | null>(null);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [customerError, setCustomerError] = useState(false);

  useEffect(() => {
    if (!selectedItem) return;

    setFoodData([]);
    setAddressText("");
    setCustomerDetail(null);

    // Yanıt gelmeden başka sipariş seçilirse eski yanıt yeni modalın üstüne yazılmasın.
    let stale = false;

    // Geçmiş siparişler ucu müşteriyi yüklemiyor, yalnız id'sini veriyor. Gömülü gelmediyse
    // Auth/CustomerDetails ile çekilir (RestourantAdmin ve SuperAdmin her müşteriyi okuyabilir).
    const c = selectedItem.preOrder?.customer;
    const customerId = selectedItem.preOrder?.customerId;
    setCustomerError(false);
    if (c) {
      setCustomerDetail({ customerName: c.customerName, customerSurName: c.customerSurName, customerTelNo: c.customerTelNo });
      setLoadingCustomer(false);
    } else if (customerId) {
      setLoadingCustomer(true);
      getCustomerDetails(customerId)
        .then((res) => {
          if (stale) return;
          const d = res.data;
          setCustomerDetail({
            customerName: d?.customerName ?? "",
            customerSurName: d?.customerSurName ?? "",
            customerTelNo: d?.customerTelNo ?? "",
          });
        })
        .catch(() => { if (!stale) setCustomerError(true); })
        .finally(() => { if (!stale) setLoadingCustomer(false); });
    } else {
      setLoadingCustomer(false);
    }

    const status = orderStatuses[selectedItem.orderStatusId];
    if (status) setOrderStatus(status);

    // Adres kaydı silinmiş siparişte customerAddress null gelir (CustomerAddressId nullable).
    const address = selectedItem.customerAddress;
    if (address?.townId) {
      setLoadingAddress(true);
      getTownById(address.townId)
        .then((res) => {
          if (stale) return;
          const place = [res.data?.townNameTr, res.data?.city?.cityNameTr].filter(Boolean).join(" / ");
          setAddressText([address.customerAddressDescription, place].filter(Boolean).join(" — "));
        })
        .catch(() => { if (!stale) setAddressText(address.customerAddressDescription ?? ""); })
        .finally(() => { if (!stale) setLoadingAddress(false); });
    } else {
      setLoadingAddress(false);
      setAddressText(address?.customerAddressDescription ?? "");
    }

    // Menüden silinmiş bir ürün 404 döner; tek başarısız istek bütün listeyi düşürmesin.
    // Sıra korunur (foodData[index] satırla eşleşir), bulunamayan ürün undefined kalır.
    const lines = selectedItem.preOrder?.foodPreOrder ?? [];
    Promise.all(
      lines.map((item) =>
        getFoodById(item.foodId).then((res) => res.data).catch(() => undefined)
      )
    ).then((results) => { if (!stale) setFoodData(results as IFood[]); });

    return () => { stale = true; };
  }, [selectedItem]);

  if (!selectedItem) return null;

  const task = orderStatus?.task ?? -1;
  const meta = TASK_META[task] ?? TASK_META[0];
  const customer = selectedItem.preOrder?.customer;
  const note = selectedItem.orderNote;

  return (
    <Modal show={isOpen} onHide={onClose} size="lg" centered>
      {/* Header */}
      <Modal.Header
        closeButton
        style={{ background: meta.bg, borderBottom: `2px solid ${meta.border}`, paddingBottom: '12px' }}
      >
        <div className="w-100">
          {/* Sipariş no + tarih */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-muted fw-semibold" style={{ fontSize: '0.82rem' }}>
              <i className="bi bi-hash me-1"></i>{selectedItem.orderNameUniqe}
            </span>
            <span className="text-muted" style={{ fontSize: '0.82rem' }}>
              <i className="bi bi-calendar3 me-1"></i>{formatDateToDayMonthYear(selectedItem.orderDate)}
            </span>
          </div>
          {/* Durum badge */}
          <div className="d-flex align-items-center gap-2">
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: meta.color,
                color: '#fff',
                borderRadius: '10px',
                padding: '5px 14px',
                fontWeight: 700,
                fontSize: '0.9rem',
              }}
            >
              <i className={`bi ${meta.icon}`}></i>
              {meta.label}
            </span>
            <span className="fw-bold" style={{ color: '#e67e00', fontSize: '1.25rem' }}>
              {selectedItem.orderPrice} TL
            </span>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body style={{ padding: '20px 24px' }}>
        <div className="row g-3">

          {/* Müşteri Bilgileri */}
          <div className="col-md-6">
            <div
              className="p-3 rounded-3 h-100"
              style={{ background: '#f8faff', border: '1.5px solid #e0eaff' }}
            >
              <div className="fw-semibold mb-2" style={{ color: '#1d4ed8', fontSize: '0.82rem' }}>
                <i className="bi bi-person-fill me-2"></i>MÜŞTERİ BİLGİLERİ
              </div>
              {loadingCustomer ? (
                <span className="text-muted small">
                  <span className="spinner-border spinner-border-sm me-1" />Yükleniyor...
                </span>
              ) : (
                <>
                  <div className="fw-bold mb-1" style={{ fontSize: '0.95rem', color: '#111827' }}>
                    {`${customerDetail?.customerName ?? customer?.customerName ?? ""} ${customerDetail?.customerSurName ?? customer?.customerSurName ?? ""}`.trim() || "—"}
                  </div>
                  {(customerDetail?.customerTelNo || customer?.customerTelNo) && (
                    <a
                      href={`tel:${customerDetail?.customerTelNo || customer?.customerTelNo}`}
                      className="text-decoration-none"
                      style={{ fontSize: '0.85rem', color: '#374151' }}
                    >
                      <i className="bi bi-telephone-fill me-1 text-primary"></i>
                      {customerDetail?.customerTelNo || customer?.customerTelNo}
                    </a>
                  )}
                  {customerError && (
                    <div className="small" style={{ color: '#b45309' }}>
                      <i className="bi bi-exclamation-triangle me-1"></i>Müşteri bilgisi alınamadı.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Adres */}
          <div className="col-md-6">
            <div
              className="p-3 rounded-3 h-100"
              style={{ background: '#fffbf0', border: '1.5px solid #fde68a' }}
            >
              <div className="fw-semibold mb-2" style={{ color: '#92400e', fontSize: '0.82rem' }}>
                <i className="bi bi-geo-alt-fill me-2"></i>TESLİMAT ADRESİ
              </div>
              {loadingAddress ? (
                <span className="text-muted small">
                  <span className="spinner-border spinner-border-sm me-1" />Yükleniyor...
                </span>
              ) : (
                <div style={{ fontSize: '0.88rem', color: '#374151', lineHeight: 1.5 }}>
                  {addressText || '—'}
                </div>
              )}
              {selectedItem.customerAddress?.customerAddressNote && (
                <div className="mt-1 text-muted" style={{ fontSize: '0.78rem' }}>
                  <i className="bi bi-info-circle me-1"></i>
                  {selectedItem.customerAddress.customerAddressNote}
                </div>
              )}
            </div>
          </div>

          {/* Sipariş Notu */}
          {note && (
            <div className="col-12">
              <div
                className="p-2 px-3 rounded-3"
                style={{ background: '#f0fdf4', border: '1.5px solid #bbf7d0', fontSize: '0.85rem', color: '#166534' }}
              >
                <i className="bi bi-chat-left-text-fill me-2"></i>
                <strong>Not:</strong> {note}
              </div>
            </div>
          )}

          {/* Sipariş Kalemleri */}
          <div className="col-12">
            <div
              className="p-3 rounded-3"
              style={{ background: '#fafafa', border: '1.5px solid #e5e7eb' }}
            >
              <div className="fw-semibold mb-3" style={{ color: '#374151', fontSize: '0.82rem' }}>
                <i className="bi bi-bag-fill me-2 text-warning"></i>SİPARİŞ KALEMLERİ
                <span className="badge ms-2" style={{ background: '#fef9c3', color: '#854d0e', borderRadius: '20px', fontSize: '0.72rem' }}>
                  {selectedItem.preOrder?.foodPreOrder?.length ?? 0} ürün
                </span>
              </div>

              <div className="d-flex flex-column gap-2">
                {selectedItem.preOrder?.foodPreOrder?.map((item, index) => {
                  const food = foodData[index];
                  // Sipariş anında sabitlenen fiyat esas alınır; o anki menü fiyatı yalnızca
                  // eski siparişler için (unitPrice hiç kaydedilmemişse) yedek olarak kullanılır.
                  const unitPrice = item.unitPrice || Number(food?.foodPrice ?? item.food?.foodPrice ?? 0);
                  const discountedUnitPrice = item.discountedUnitPrice ?? unitPrice;
                  const hasDiscount = discountedUnitPrice < unitPrice;
                  const lineTotal = !isNaN(discountedUnitPrice) && discountedUnitPrice >= 0 ? discountedUnitPrice * item.foodQuantity : null;
                  const extras = parseMenuExtras(item.menuUpgradeNotes).filter(e => e.name && e.quantity > 0);
                  return (
                    <div
                      key={index}
                      className="p-3 rounded-3"
                      style={{ background: '#fff', border: '1px solid #e5e7eb' }}
                    >
                      {/* Yemek adı + adet + fiyat */}
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="fw-semibold" style={{ color: '#111827', fontSize: '0.92rem' }}>
                          <i className="bi bi-dot text-warning" style={{ fontSize: '1.2rem' }}></i>
                          {food?.foodNameTr ?? item.food?.foodNameTr ?? '—'}
                          {hasDiscount && (
                            <span className="ms-2" style={{ fontSize: '0.62rem', fontWeight: 700, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '1px 6px' }}>
                              <i className="bi bi-percent"></i> kampanya
                            </span>
                          )}
                        </div>
                        <div className="d-flex align-items-center gap-1" style={{ fontSize: '0.82rem', fontWeight: 600, color: '#6b7280', whiteSpace: 'nowrap' }}>
                          {lineTotal != null ? (
                            <>
                              {hasDiscount && (
                                <span style={{ textDecoration: 'line-through', color: '#9ca3af', fontWeight: 400 }}>
                                  {(unitPrice * item.foodQuantity).toFixed(2)} TL
                                </span>
                              )}
                              <span>{item.foodQuantity} × {discountedUnitPrice.toFixed(2)}</span>
                              <span style={{ color: '#9ca3af' }}>=</span>
                              <span style={{ color: '#e67e00' }}>{lineTotal.toFixed(2)} TL</span>
                            </>
                          ) : (
                            <span style={{ background: '#f3f4f6', color: '#374151', borderRadius: '20px', padding: '2px 10px' }}>
                              {item.foodQuantity} adet
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Ekstralar */}
                      {extras.length > 0 && (
                        <div className="mt-2 ps-3">
                          {extras.map((extra, i) => (
                            <div key={i} className="d-flex justify-content-between align-items-center py-1" style={{ borderTop: '1px dashed #e5e7eb', fontSize: '0.8rem', color: '#6b7280' }}>
                              <span>
                                <i className="bi bi-plus-circle me-1 text-primary"></i>
                                {extra.name} × {extra.quantity}
                              </span>
                              <span className="fw-semibold" style={{ color: '#e67e00' }}>
                                {extra.total.toFixed(2)} TL
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {(selectedItem.itemDiscountTotal ?? 0) > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-3 pt-2" style={{ borderTop: '1px dashed #e5e7eb' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.82rem', fontWeight: 700, color: '#b45309' }}>
                    <i className="bi bi-percent"></i> Kampanya İndirimi
                  </span>
                  <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#b45309' }}>
                    -{selectedItem.itemDiscountTotal} TL
                  </span>
                </div>
              )}
            </div>
          </div>

        </div>
      </Modal.Body>

      {/* Footer */}
      <Modal.Footer style={{ background: '#f9fafb', borderTop: '1.5px solid #e5e7eb', padding: '12px 24px' }}>
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="fw-bold" style={{ fontSize: '1.05rem', color: '#111827' }}>
            Toplam Tutar:
            <span style={{ color: '#e67e00', marginLeft: '8px' }}>{selectedItem.orderPrice} TL</span>
          </div>
          <button
            onClick={onClose}
            className="btn"
            style={{ borderRadius: '10px', background: '#f3f4f6', color: '#374151', fontWeight: 600, border: '1.5px solid #e5e7eb' }}
          >
            <i className="bi bi-x-lg me-1"></i>Kapat
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default StandardModal;